import { useState } from "react";
import { Globe, ShieldCheck, Clock, Trash2, Copy, Check } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { WriteGuard } from "@/components/billing/write-guard";
import { useDomainActions, useDomains } from "@/hooks/queries/use-organization";
import type { OrganizationDomain } from "@/lib/api/organization";

function errorText(error: unknown): string {
  return error instanceof Error ? error.message : "Something went wrong.";
}

function CopyValue({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex items-center gap-2">
      <code className="min-w-0 flex-1 truncate rounded bg-muted px-2 py-1 text-xs">{value}</code>
      <Button
        variant="ghost"
        size="sm"
        aria-label={`Copy ${value}`}
        onClick={() => {
          void navigator.clipboard.writeText(value).then(() => {
            setCopied(true);
            setTimeout(() => {
              setCopied(false);
            }, 1500);
          });
        }}
      >
        {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      </Button>
    </div>
  );
}

function DomainRow({ domain }: { domain: OrganizationDomain }) {
  const { verify, setAutoJoin, remove } = useDomainActions();
  const [rowError, setRowError] = useState<string | null>(null);
  const busy = verify.isPending || setAutoJoin.isPending || remove.isPending;

  return (
    <li className="space-y-3 py-4 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-muted-foreground" aria-hidden />
          <span className="font-medium">{domain.domain}</span>
          {domain.verified ? (
            <Badge variant="outline" className="gap-1 border-green-500/30 text-green-400">
              <ShieldCheck className="h-3 w-3" /> Verified
            </Badge>
          ) : (
            <Badge variant="outline" className="gap-1 border-amber-500/30 text-amber-400">
              <Clock className="h-3 w-3" /> Pending DNS check
            </Badge>
          )}
        </div>
        <WriteGuard>
          <div className="flex items-center gap-1">
            {!domain.verified && (
              <Button
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => {
                  setRowError(null);
                  verify.mutate(domain.id, { onError: (e) => { setRowError(errorText(e)); } });
                }}
              >
                {verify.isPending ? "Checking..." : "Verify"}
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              aria-label={`Remove ${domain.domain}`}
              disabled={busy}
              onClick={() => {
                setRowError(null);
                remove.mutate(domain.id, { onError: (e) => { setRowError(errorText(e)); } });
              }}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </WriteGuard>
      </div>

      {!domain.verified && (
        <div className="space-y-2 rounded-lg border border-border p-3 text-sm">
          <p className="text-muted-foreground">
            Add this TXT record in your DNS provider, then click Verify. DNS changes can take a few
            minutes.
          </p>
          <div className="grid gap-2 sm:grid-cols-[80px_1fr] sm:items-center">
            <span className="text-xs text-muted-foreground">Host</span>
            <CopyValue value={domain.txtRecord.host} />
            <span className="text-xs text-muted-foreground">Value</span>
            <CopyValue value={domain.txtRecord.value} />
          </div>
        </div>
      )}

      {domain.verified && (
        <div className="flex items-center gap-2">
          <Checkbox
            id={`autojoin-${domain.id}`}
            checked={domain.autoJoin}
            disabled={busy}
            onCheckedChange={(checked) => {
              setRowError(null);
              setAutoJoin.mutate(
                { id: domain.id, autoJoin: checked },
                { onError: (e) => { setRowError(errorText(e)); } }
              );
            }}
            aria-label={`Auto-join for ${domain.domain}`}
          />
          <label htmlFor={`autojoin-${domain.id}`} className="text-sm text-muted-foreground">
            New people signing up with an @{domain.domain} address join this organization automatically
          </label>
        </div>
      )}

      {rowError && (
        <p className="text-xs text-destructive" role="alert">
          {rowError}
        </p>
      )}
    </li>
  );
}

/**
 * Company domains (domain auto-join). An org admin claims "acme.com", proves
 * it with a DNS TXT record, and from then on colleagues who sign up with an
 * @acme.com address (Google, Microsoft work account, or a verified email
 * signup) join this organization instead of creating their own.
 */
export function CompanyDomains() {
  const { data: domains, isLoading, error } = useDomains();
  const { claim } = useDomainActions();
  const [draft, setDraft] = useState("");
  const [claimError, setClaimError] = useState<string | null>(null);

  if (error) return null; // Not an org admin — the server refused; render nothing.

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-base font-medium">Company domains</h2>
        <p className="text-sm text-muted-foreground">
          Verify your company&apos;s email domain so colleagues who sign up join this organization
          automatically. Public email providers (gmail.com, outlook.com, …) can&apos;t be claimed.
        </p>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading domains...</p>
      ) : (
        <>
          {domains && domains.length > 0 && (
            <ul className="divide-y divide-border">
              {domains.map((d) => (
                <DomainRow key={d.id} domain={d} />
              ))}
            </ul>
          )}
          <WriteGuard>
            <form
              className="flex flex-col gap-2 sm:flex-row"
              onSubmit={(e) => {
                e.preventDefault();
                setClaimError(null);
                claim.mutate(draft, {
                  onSuccess: () => {
                    setDraft("");
                  },
                  onError: (err) => {
                    setClaimError(errorText(err));
                  },
                });
              }}
            >
              <Input
                aria-label="Company domain"
                placeholder="acme.com"
                value={draft}
                onChange={(e) => {
                  setDraft(e.target.value);
                }}
                className="sm:max-w-xs"
                required
              />
              <Button type="submit" size="sm" disabled={claim.isPending || draft.trim().length === 0}>
                Add domain
              </Button>
            </form>
          </WriteGuard>
          {claimError && (
            <p className="text-xs text-destructive" role="alert">
              {claimError}
            </p>
          )}
        </>
      )}
    </section>
  );
}
